import { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fotoUrlHolen } from "../dienste/fotos.js";
import { gesamtFlaschen, jahrgangTitel } from "../kern/fachlogik.js";
import { FLASCHENGROESSEN, LISTEN_GRENZEN, TYPEN } from "../kern/konstanten.js";
import { KurzListe } from "./begrenzt.jsx";
import { t } from "../i18n/index.js";


/** Zeigt ein Etikettenfoto. Ein Pfad mit dem Präfix `wartend:` steht
    für eine Aufnahme, die ohne Netz gemacht wurde und noch in der
    Warteschlange liegt — sie wird aus dem Gerät selbst angezeigt und
    als solche gekennzeichnet, damit niemand sie für übertragen hält. */
function FotoBild({ pfad, alt, className }) {
  const [url, setUrl] = useState(null);
  const [fehler, setFehler] = useState(false);
  const wartet = typeof pfad === "string" && pfad.startsWith("wartend:");
  useEffect(() => {
    let ab = false;
    setUrl(null); setFehler(false);
    if (!pfad) return;
    fotoUrlHolen(pfad).then((u) => { if (!ab) setUrl(u); }).catch(() => { if (!ab) setFehler(true); });
    return () => { ab = true; };
  }, [pfad]);
  if (fehler) return <div className={"wl-fotoplatz " + (className || "")}>{t("Foto nicht abrufbar")}</div>;
  if (!url) return <div className={"wl-fotoplatz " + (className || "")}>{t("Lädt …")}</div>;
  const bild = <img className={"wl-foto " + (className || "")} src={url} alt={alt} loading="lazy" />;
  if (!wartet) return bild;
  return (
    <span className="wl-fotohuelle">
      {bild}
      <span className="wl-fotowartet">{t("wartet auf Übertragung")}</span>
    </span>
  );
}


/* §4 ─ UI-Primitive ───────────────────────────────────────────── */

function Btn({ variante = "linie", className = "", ...props }) {
  return <button className={`wl-btn wl-btn--${variante} ${className}`} {...props} />;
}


function TextKnopf({ className = "", ...props }) {
  return <button className={`wl-textknopf ${className}`} {...props} />;
}


function Feld({ label, breit = false, children }) {
  return (
    <label className={"wl-feld" + (breit ? " voll" : "")}>
      <span className="wl-feldname">{label}</span>
      {children}
    </label>
  );
}


/* `ref` wird durchgereicht — die Werkzeugleiste setzt den Fokus nach
   dem Löschen der Suche zurück ins Feld. Ohne forwardRef landete das
   ref am Funktionsbauteil und wäre wirkungslos. */
const Eingabe = forwardRef((props, ref) => <input ref={ref} className="wl-eingabe" {...props} />);
Eingabe.displayName = "Eingabe";

const Auswahl = (props) => <select className="wl-eingabe" {...props} />;


/* Eingabefeld mit Autovervollständigung aus bestehenden Werten.
   `vorschlaege` ist eine Liste eindeutiger Strings; die native
   datalist filtert beim Tippen (z. B. „C" → alle mit C beginnenden). */
let vorschlagZaehler = 0;

function VorschlagEingabe({ vorschlaege = [], className = "", ...props }) {
  const idRef = useRef(null);
  if (idRef.current == null) idRef.current = "wl-vorschlag-" + (++vorschlagZaehler);
  const liste = idRef.current;
  return (
    <>
      <input className={"wl-eingabe" + (className ? " " + className : "")} list={liste} autoComplete="off" {...props} />
      <datalist id={liste}>
        {vorschlaege.map((v) => <option key={v} value={v} />)}
      </datalist>
    </>
  );
}


/** Durchsuchbare Auswahl für lange Listen (Winzer, Regionen).
    Ersetzt <Auswahl> eins zu eins: onChange erhält ein Ereignis mit
    { target: { value } }. Optionen: [{ wert, label, neben?, betont? }] */
function SuchAuswahl({ value, onChange, optionen, platzhalter = "Suchen oder wählen …", className = "" }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const [aktiv, setAktiv] = useState(0);
  const huelle = useRef(null);
  const feld = useRef(null);

  const gewaehlt = optionen.find((o) => o.wert === value) || null;
  const q = text.trim().toLowerCase();
  /* Optionen mit `immer` überstehen die Suche — das sind die Auswege
     („neu anlegen", „ohne Zuordnung"). Gerade wenn die Eingabe zu
     nichts passt, werden sie gebraucht; sie mit wegzufiltern hieße,
     dem Nutzer im entscheidenden Moment die Tür zuzuziehen. */
  const treffer = q
    ? optionen.filter((o) => o.immer || `${o.label} ${o.neben || ""}`.toLowerCase().includes(q))
    : optionen;
  const nurAuswege = q && treffer.every((o) => o.immer);

  const schliesse = () => { setOffen(false); setText(""); };
  const oeffne = () => {
    setOffen(true); setText("");
    setAktiv(Math.max(0, optionen.findIndex((o) => o.wert === value)));
  };
  const waehle = (o) => {
    onChange({ target: { value: o.wert } });
    schliesse();
    if (feld.current) feld.current.blur();
  };

  /* Klick außerhalb schließt die Liste. */
  useEffect(() => {
    if (!offen) return;
    const zu = (e) => { if (huelle.current && !huelle.current.contains(e.target)) schliesse(); };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, [offen]);

  const taste = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!offen) return oeffne();
      setAktiv((i) => Math.min(treffer.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAktiv((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && offen && treffer[aktiv]) {
      e.preventDefault(); waehle(treffer[aktiv]);
    } else if (e.key === "Escape" && offen) {
      e.preventDefault(); schliesse();
    }
  };

  return (
    <div className={"wl-suchauswahl" + (className ? " " + className : "")} ref={huelle}>
      <input
        ref={feld} className="wl-eingabe wl-suchfeld" role="combobox" aria-expanded={offen}
        aria-autocomplete="list" autoComplete="off"
        value={offen ? text : (gewaehlt ? gewaehlt.label : "")}
        placeholder={offen && gewaehlt ? gewaehlt.label : platzhalter}
        onChange={(e) => { setText(e.target.value); setAktiv(0); if (!offen) setOffen(true); }}
        onFocus={oeffne}
        onKeyDown={taste}
        onBlur={() => schliesse()}
      />
      <span className="wl-suchpfeil" aria-hidden="true">▾</span>
      {offen && (
        <ul className="wl-suchliste" role="listbox">
          {treffer.length === 0 ? (
            <li className="wl-suchleer">{t("Kein Treffer für „{q}“", { q: text.trim() })}</li>
          ) : (
            <>
              {nurAuswege && <li className="wl-suchleer">{t("Kein Treffer für „{q}“", { q: text.trim() })}</li>}
              {treffer.map((o, i) => (
            <li key={o.wert} role="option" aria-selected={o.wert === value}
              className={"wl-suchopt"
                + (i === aktiv ? " ist-aktiv" : "")
                + (o.wert === value ? " ist-gewaehlt" : "")
                + (o.betont ? " ist-betont" : "")}
              onMouseDown={(e) => { e.preventDefault(); waehle(o); }}
              onMouseEnter={() => setAktiv(i)}>
              <span className="wl-suchopt-label">{o.label}</span>
              {o.neben && <span className="wl-suchopt-neben">{o.neben}</span>}
            </li>
              ))}
            </>
          )}
        </ul>
      )}
    </div>
  );
}

const Textbereich = (props) => <textarea className="wl-eingabe" rows={4} {...props} />;


function TypPunkt({ typ }) {
  const t = TYPEN[typ];
  return (
    <span className="wl-typ">
      <i style={{ background: t ? t.farbe : "#999" }} aria-hidden="true" />
      {t ? t.label : typ}
    </span>
  );
}


/* `titel` ist deutscher Quelltext und läuft hier durch t() — so muss
   nicht jede Aufrufstelle daran denken. Zusammengesetzte Titel
   („Entnahme · Scharzhofberger 2018") übersetzen ihren festen Teil
   bereits selbst; t() gibt sie unverändert zurück. */
function Modal({ titel, onClose, children }) {
  const panelRef = useRef(null);
  const beschriftung = t(titel);

  /* Fokusführung: Fokus in den Dialog holen, Tab im Dialog halten,
     beim Schließen zum auslösenden Element zurückkehren. */
  useEffect(() => {
    const vorher = document.activeElement;
    const fokusierbare = () => panelRef.current
      ? [...panelRef.current.querySelectorAll("button, input, select, textarea")].filter((el) => !el.disabled)
      : [];
    /* Wohin der Fokus springt, entscheidet die Art des Dialogs — und
       die verrät sein erstes Bedienelement:

       Ein Formular beginnt mit einem Eingabefeld („Wein anlegen" →
       Name). Dort hinein zu springen spart einen Griff.

       Eine Übersicht beginnt mit Schaltern oder einer Auswahl (der
       Trinkverlauf mit seinen Filterknöpfen). Hier wäre jeder
       Sprung ein Übergriff: In ein <select> gefahren öffnet das
       Telefon sofort das Auswahlrad — der Trinkverlauf fragte so
       jedes Mal nach dem Jahr, ohne dass jemand danach verlangt
       hätte. Ein Textfeld wiederum klappt die Tastatur auf.

       Also: nur wenn das ERSTE Element ein Textfeld ist, wird es
       angesprungen; sonst bekommt der Dialog selbst den Fokus. Die
       Tab-Falle und Escape greifen unverändert. */
    const liste = fokusierbare().filter((el) => !el.classList.contains("wl-schliessen"));
    const erstes = liste[0];
    /* Eine Suchauswahl ist zwar ein <input>, klappt beim Fokus aber
       ihre Liste auf und verdeckt damit den halben Dialog. Sie zählt
       hier deshalb nicht als Textfeld — zumal sie oft vorbelegt ist
       und gar nicht angefasst werden muss. */
    const istSuchauswahl = erstes && erstes.closest && erstes.closest(".wl-suchauswahl");
    const einstieg = erstes && !istSuchauswahl
      && (erstes.tagName === "INPUT" || erstes.tagName === "TEXTAREA")
      ? erstes : null;
    if (einstieg) einstieg.focus();
    else if (panelRef.current) panelRef.current.focus();

    const h = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab") return;
      const aktuelle = fokusierbare();
      if (aktuelle.length === 0) return;
      const erste = aktuelle[0];
      const letzte = aktuelle[aktuelle.length - 1];
      if (e.shiftKey && document.activeElement === erste) { e.preventDefault(); letzte.focus(); }
      else if (!e.shiftKey && document.activeElement === letzte) { e.preventDefault(); erste.focus(); }
    };
    window.addEventListener("keydown", h);
    return () => {
      window.removeEventListener("keydown", h);
      if (vorher && typeof vorher.focus === "function") vorher.focus();
    };
  }, [onClose]);

  return (
    <div className="wl-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wl-modal" role="dialog" aria-modal="true" aria-label={beschriftung} tabIndex={-1} ref={panelRef}>
        <div className="wl-modalkopf">
          <h2>{beschriftung}</h2>
          <button className="wl-schliessen" aria-label={t("Schließen")} onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}


/** Zweistufiger Löschknopf — ersetzt window.confirm, das in
    Sandbox-Umgebungen blockiert sein kann. Entschärft sich nach 3,5 s. */
function LoeschenKnopf({ onDelete, label = "Löschen", disabledGrund = null }) {
  const [scharf, setScharf] = useState(false);
  useEffect(() => {
    if (!scharf) return;
    const t = setTimeout(() => setScharf(false), 3500);
    return () => clearTimeout(t);
  }, [scharf]);
  if (disabledGrund) {
    return <Btn variante="ghost" disabled title={disabledGrund}>{label}</Btn>;
  }
  return (
    <Btn
      variante={scharf ? "gefahrScharf" : "gefahr"}
      onClick={() => (scharf ? onDelete() : setScharf(true))}
    >
      {scharf ? "Wirklich löschen?" : label}
    </Btn>
  );
}


/** Dezente Textvariante des zweistufigen Löschens für Tabellenzeilen. */
function TextLoeschen({ onDelete, disabledGrund = null }) {
  const [scharf, setScharf] = useState(false);
  useEffect(() => {
    if (!scharf) return;
    const t = setTimeout(() => setScharf(false), 3500);
    return () => clearTimeout(t);
  }, [scharf]);
  if (disabledGrund) {
    return <span className="wl-textknopf ist-inaktiv" title={disabledGrund}>{t("Löschen")}</span>;
  }
  return (
    <TextKnopf
      className={scharf ? "ist-scharf" : ""}
      onClick={() => (scharf ? onDelete() : setScharf(true))}
    >
      {scharf ? "Wirklich?" : "Löschen"}
    </TextKnopf>
  );
}


function Leer({ text, aktion = null }) {
  return (
    <div className="wl-leer">
      <p>{text}</p>
      {aktion}
    </div>
  );
}


/** Dreipunkt-Zeilenmenü. Gefahr-Einträge verlangen einen zweiten
    Klick zur Bestätigung; Positionierung fix am Viewport, damit das
    Menü nicht von scrollbaren Tabellen beschnitten wird. */
/* Dreipunktmenü einer Tabellenzeile.

   Zwei Fallstricke stecken hier drin, beide nur auf dem Telefon
   sichtbar:

   1. Die Tabellen liegen in `.wl-scroll` (waagerecht schiebbar). Ein
      `position: fixed` entkommt dieser Überlaufkante zwar laut Norm,
      WebKit auf iOS beschneidet es aber trotzdem — dort blieb das
      Menü im Jahrgangsbereich stecken und war bei einem einzigen
      Jahrgang praktisch unbedienbar. Deshalb hängt die Liste jetzt
      per Portal direkt am <body>: Kein Vorfahr kann sie mehr
      beschneiden — allerdings NICHT an <body>: Die Farb- und
      Schriftvariablen hängen an `.wl-root`, außerhalb davon wäre das
      Menü durchsichtig und in der falschen Schrift. Ziel ist deshalb
      `.wl-root` selbst; sie liegt über jedem Überlaufbereich und
      erzeugt keinen eigenen Bezugsrahmen für `position: fixed`.
   2. Das Antippen des Knopfes am rechten Rand schiebt den
      Tabellencontainer mit — früher schloss dieses Scroll-Ereignis
      das gerade geöffnete Menü sofort wieder. Jetzt wird die Liste
      nachgeführt und nur dann geschlossen, wenn der Knopf selbst aus
      dem Sichtfeld läuft. */
function ZeilenMenue({ eintraege, beschriftung = "Aktionen", knopfLabel = null, knopfVariante = "linie" }) {
  const [offen, setOffen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, oben: false, breite: 220 });
  const [scharf, setScharf] = useState(null);
  const knopfRef = useRef(null);
  const listeRef = useRef(null);

  /* Lage aus dem Knopf ableiten. `null` heißt: Knopf ist weg oder aus
     dem Sichtfeld gelaufen — dann gehört das Menü geschlossen. */
  const lage = () => {
    const knopf = knopfRef.current;
    if (!knopf) return null;
    const r = knopf.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) return null;
    const breite = 220;
    const oben = window.innerHeight - r.bottom < 240;
    /* Rechte Kante am Knopf ausrichten, aber im Sichtfeld halten,
       damit das Menü auf schmalen Bildschirmen nicht abgeschnitten wird. */
    const left = Math.max(8, Math.min(r.right - breite, window.innerWidth - breite - 8));
    return { top: oben ? r.top - 4 : r.bottom + 4, left, oben, breite };
  };

  const umschalten = () => {
    const l = lage();
    if (l) setPos(l);
    setScharf(null);
    setOffen((o) => !o);
  };

  useEffect(() => {
    if (!offen) return;
    const zu = () => { setOffen(false); setScharf(null); };
    const klick = (e) => {
      const imKnopf = knopfRef.current && knopfRef.current.contains(e.target);
      const inListe = listeRef.current && listeRef.current.contains(e.target);
      if (!imKnopf && !inListe) zu();
    };
    const taste = (e) => { if (e.key === "Escape") zu(); };
    const nachfuehren = () => { const l = lage(); if (l) setPos(l); else zu(); };
    window.addEventListener("mousedown", klick);
    window.addEventListener("touchstart", klick);
    window.addEventListener("keydown", taste);
    window.addEventListener("scroll", nachfuehren, true);
    window.addEventListener("resize", nachfuehren);
    return () => {
      window.removeEventListener("mousedown", klick);
      window.removeEventListener("touchstart", klick);
      window.removeEventListener("keydown", taste);
      window.removeEventListener("scroll", nachfuehren, true);
      window.removeEventListener("resize", nachfuehren);
    };
  }, [offen]);

  /* Tastatur: erster Menüpunkt erhält beim Öffnen den Fokus */
  useEffect(() => {
    if (!offen || !listeRef.current) return;
    const erster = listeRef.current.querySelector("button.wl-menuepunkt");
    if (erster) erster.focus();
  }, [offen]);

  /* Erst nach dem Öffnen ausgewertet — dann steht der Knopf im DOM. */
  const zielFuerPortal = () => {
    if (typeof document === "undefined") return null;
    const knopf = knopfRef.current;
    return (knopf && knopf.closest(".wl-root")) || document.body;
  };

  const liste = (
    <span className="wl-menueliste" role="menu" ref={listeRef}
      style={{ top: pos.top, left: pos.left, width: pos.breite, transform: pos.oben ? "translateY(-100%)" : "none" }}>
      {eintraege.map((eintrag, i) => eintrag.disabledGrund ? (
        <span key={i} className="wl-menuepunkt ist-inaktiv" title={eintrag.disabledGrund}>{eintrag.label}</span>
      ) : (
        <button key={i} role="menuitem"
          className={"wl-menuepunkt" + (eintrag.gefahr ? " ist-gefahr" : "")}
          onClick={() => {
            if (eintrag.gefahr && scharf !== i) { setScharf(i); return; }
            setOffen(false); setScharf(null); eintrag.onClick();
          }}>
          {eintrag.gefahr && scharf === i ? t("Wirklich löschen?") : eintrag.label}
        </button>
      ))}
    </span>
  );

  return (
    <span className="wl-menue">
      {knopfLabel ? (
        <button ref={knopfRef} className={`wl-btn wl-btn--${knopfVariante}`} aria-label={beschriftung} aria-haspopup="menu"
          aria-expanded={offen} onClick={umschalten}>{knopfLabel}</button>
      ) : (
        <button ref={knopfRef} className="wl-menueknopf" aria-label={beschriftung} aria-haspopup="menu"
          aria-expanded={offen} onClick={umschalten}>⋯</button>
      )}
      {offen && zielFuerPortal() && createPortal(liste, zielFuerPortal())}
    </span>
  );
}


/** Gesamtbestand mit Größenaufteilung, sofern sie vom Standard abweicht. */
function BestandAnzeige({ jahrgang }) {
  const teile = FLASCHENGROESSEN.filter((g) => ((jahrgang.bestaende || {})[g.key] || 0) > 0);
  const gesamt = gesamtFlaschen(jahrgang);
  const nurStandard = teile.length === 1 && teile[0].key === "0.75";
  return (
    <span className="wl-bestandanzeige">
      <span className="num wl-bestandzahl">{gesamt}</span>
      {gesamt > 0 && !nurStandard && (
        <span className="wl-nebenzeile">
          {teile.map((g) => `${jahrgang.bestaende[g.key]} × ${g.kurz}`).join(" · ")}
        </span>
      )}
    </span>
  );
}


/** Normierter Vergleichsbalken: macht Bewertungen unterschiedlicher
    Schemata optisch vergleichbar (0–1 über min/max des Schemas). */
function KritikBalken({ anteil }) {
  return (
    <span className="wl-balken" aria-hidden="true">
      <i style={{ width: `${Math.round((anteil || 0) * 100)}%` }} />
    </span>
  );
}


function Seitenkopf({ krume = null, titel, unterzeile = null, aktionen = null }) {
  return (
    <div className="wl-seitenkopf">
      <div>
        {krume && <div className="wl-brotkrume">{krume}</div>}
        <h1 className="wl-titel">{titel}</h1>
        {unterzeile && <p className="wl-unterzeile-seite">{unterzeile}</p>}
      </div>
      {aktionen && <div className="wl-aktionen">{aktionen}</div>}
    </div>
  );
}


/** Zurück-Leiste — nur sichtbar, wenn ein Navigationsverlauf besteht. */
function ZurueckLeiste({ sichtbar, onBack }) {
  if (!sichtbar) return null;
  return (
    <div className="wl-zurueck">
      <button className="wl-zurueckknopf" onClick={onBack}>
        <span aria-hidden="true">←</span> {t("Zurück")}
      </button>
    </div>
  );
}


/** Werkzeugleiste mit Suche, optional einklappbaren Filtern,
    dem geteilten „Nur mit Bestand"-Schalter und der Primäraktion.
    Die Filterauswahl erscheint erst auf Klick und bleibt sonst
    eingeklappt. Ein Punkt signalisiert aktive Filter. */
/* Trichter — das übliche Zeichen für „Filter“. */
const SymbolFilter = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
    <path d="M2 3h12l-4.6 5.4v4.2l-2.8 1.6V8.4Z" fill="none" stroke="currentColor"
      strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);

/* Drei Balken abnehmender Länge — das übliche Zeichen für Sortierung. */
const SymbolSortierung = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
    <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <path d="M2.5 4h11" /><path d="M2.5 8h7.5" /><path d="M2.5 12h4" />
    </g>
  </svg>
);


function Werkzeugleiste({
  suche, setSuche, sucheLabel = "Suchen …",
  filter = null, filterAktiv = false, onReset = null,
  sortOptionen = null, sortWert, setSort,
  aktion = null,
}) {
  const [offen, setOffen] = useState(false);
  const [sortOffen, setSortOffen] = useState(false);
  const sortAktiv = !!(sortOptionen && sortWert !== sortOptionen[0].key);
  const sucheRef = useRef(null);

  /* Nach dem Löschen zurück ins Feld — wer das Kreuz drückt, will
     meist gleich etwas anderes eintippen. */
  const leeren = () => {
    setSuche("");
    if (sucheRef.current) sucheRef.current.focus();
  };

  return (
    <div className="wl-werkzeuge-block">
      <div className="wl-werkzeuge">
        <div className="wl-werkzeuge-links">
          <span className="wl-suchfeld">
            <Eingabe className="wl-suche" placeholder={sucheLabel} value={suche} ref={sucheRef}
              onChange={(e) => setSuche(e.target.value)} aria-label={sucheLabel} />
            {suche !== "" && (
              <button type="button" className="wl-suchleeren" onClick={leeren}
                aria-label={t("Suche löschen")} title={t("Suche löschen")}>×</button>
            )}
          </span>
          {/* Symbole statt Beschriftung: Trichter für Filter, Balken
              für Sortierung. Der Titel bleibt für Vorlesewerkzeuge und
              als Kurzhinweis erhalten. */}
          {filter && (
            <button className={"wl-werkzeugknopf" + (offen ? " ist-offen" : "") + (filterAktiv ? " ist-aktiv" : "")}
              aria-expanded={offen} aria-label={t("Filter")} title={t("Filter")}
              onClick={() => setOffen((o) => !o)}>
              <SymbolFilter />
              {filterAktiv && <span className="wl-filterpunkt" aria-hidden="true" />}
            </button>
          )}
          {sortOptionen && (
            <button className={"wl-werkzeugknopf" + (sortOffen ? " ist-offen" : "") + (sortAktiv ? " ist-aktiv" : "")}
              aria-expanded={sortOffen} aria-label={t("Sortierung")} title={t("Sortierung")}
              onClick={() => setSortOffen((o) => !o)}>
              <SymbolSortierung />
              {sortAktiv && <span className="wl-filterpunkt" aria-hidden="true" />}
            </button>
          )}
        </div>
        {aktion}
      </div>
      {filter && offen && (
        <div className="wl-filterleiste">
          {filter}
          {filterAktiv && onReset && (
            <TextKnopf className="ist-dezent" onClick={onReset}>{t("Filter zurücksetzen")}</TextKnopf>
          )}
        </div>
      )}
      {sortOptionen && sortOffen && (
        <div className="wl-filterleiste">
          <Auswahl className="wl-sortauswahl" value={sortWert} onChange={(e) => setSort(e.target.value)} aria-label={t("Sortierung")}>
            {sortOptionen.map((o) => <option key={o.key} value={o.key}>{t(o.label)}</option>)}
          </Auswahl>
          {sortAktiv && (
            <TextKnopf className="ist-dezent" onClick={() => setSort(sortOptionen[0].key)}>{t("Standard (Name A–Z)")}</TextKnopf>
          )}
        </div>
      )}
    </div>
  );
}


function ModalFuss({ onCancel, onSave, saveLabel = null, cancelLabel = null, links = null }) {
  return (
    <div className={"wl-modalfuss" + (links ? " hat-links" : "")}>
      {links}
      <div className="wl-modalfuss-rechts">
        <Btn variante="ghost" onClick={onCancel}>{cancelLabel || t("Abbrechen")}</Btn>
        <Btn variante="voll" onClick={onSave}>{saveLabel || t("Speichern")}</Btn>
      </div>
    </div>
  );
}


/** Menüeinträge für eine Jahrgangszeile (Keller & Weindetail). */
function jahrgangsMenue({ wein, jg, oeffneModal, loescheJahrgang, setzeFavorit }) {
  return [
    {
      label: wein.favorit ? "Lieblingsmarkierung entfernen" : "Als Lieblingswein markieren",
      onClick: () => setzeFavorit(wein.id),
    },
    {
      label: "Entnehmen",
      disabledGrund: gesamtFlaschen(jg) === 0 ? "Kein Bestand vorhanden." : null,
      onClick: () => oeffneModal({ typ: "entnahme", kontext: { weinId: wein.id, jgId: jg.id, jahr: jg.jahr } }),
    },
    {
      label: "Zugang buchen",
      onClick: () => oeffneModal({ typ: "zugang", kontext: { weinId: wein.id, jgId: jg.id, jahr: jg.jahr } }),
    },
    {
      label: "Jahrgang bearbeiten",
      onClick: () => oeffneModal({ typ: "jahrgang", objekt: jg, kontext: { weinId: wein.id, jahrgangId: jg.id } }),
    },
    {
      label: "Jahrgang löschen",
      gefahr: true,
      onClick: () => loescheJahrgang(wein.id, jg.id),
    },
  ];
}

/**
 * Jahrgänge eines Weins als einzeln verlinkte Jahre — ersetzt die bloße
 * Anzahl in den Listenansichten. Absteigend sortiert (N.V. ans Ende).
 * Bei aktivem „Nur mit Bestand" werden leere Jahrgänge ausgeblendet;
 * jede Marke springt in den passenden Jahrgang des Weins.
 */
function JahrgangsLinks({ wein, nurBestand, geheZu }) {
  let liste = [...wein.jahrgaenge];
  if (nurBestand) liste = liste.filter((jg) => gesamtFlaschen(jg) > 0);
  if (liste.length === 0) return <span className="wl-nebel">—</span>;
  /* Jüngste zuerst; N.V. (jahr 0/null) ans Ende. */
  liste.sort((a, b) => (b.jahr || 0) - (a.jahr || 0));
  /* Eine Vertikale von 30 Jahrgängen sprengte sonst die Zeile — die
     jüngsten zuerst, der Rest auf Klick. */
  return (
    <KurzListe className="wl-jahrlinks" grenze={LISTEN_GRENZEN.jahrgangsLinks.erste}
      elemente={liste.map((jg) => (
        <button key={jg.id} className="wl-jahrlink num"
          onClick={() => geheZu({ view: "jahrgang", weinId: wein.id, jgId: jg.id })}
          title={`Jahrgang ${jahrgangTitel(wein, jg)} öffnen`}>
          {jahrgangTitel(wein, jg)}
        </button>
      ))} />
  );
}

export { Auswahl, BestandAnzeige, Btn, Eingabe, Feld, FotoBild, JahrgangsLinks, KritikBalken, Leer, LoeschenKnopf, Modal, ModalFuss, Seitenkopf, SuchAuswahl, TextKnopf, TextLoeschen, Textbereich, TypPunkt, VorschlagEingabe, Werkzeugleiste, ZeilenMenue, ZurueckLeiste, jahrgangsMenue, vorschlagZaehler };
